import { Box, Chip, Grid, Typography } from "@mui/material";
import CustomToolTip from "../../../../components/CustomTooltip";
import SvgToIcon from "../../../../icons/SvgToIcon";
import { ComplexEntityTypes, ControlStatus } from "../../../../utils/constants";
import { IntegrationsMapping } from "../../../stack/integrations/IntegrationsMapping";
import ControlStatusIndicator from "../../controls/components/ControlStatusIndicator";
import InvolvedEntityChip from "../../controls/components/EntitiesSection/InvolvedEntityChip";
import {
  getEntitiesLabel,
  groupEntityIdsByType,
} from "../../controls/components/EntitiesSection/utils";

export function getCompensatingControlsColumns(
  colors,
  affectedEntityIdsFilter
) {
  return [
    {
      field: "control_id",
      headerName: "ID",
      width: 60,
      renderCell: (params) => (
        <Typography variant="caption" sx={{ marginLeft: "10px" }}>
          #{params.value}
        </Typography>
      ),
    },
    {
      field: "name",
      headerName: "Remediation Action",
      flex: 0.5,
      renderCell: (params) => (
        <Typography variant="caption" sx={{ marginLeft: "12px" }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "tool",
      headerName: "Tool",
      flex: 0.15,
      renderCell: (params) => (
        <CustomToolTip title={params.value}>
          <Box sx={{ marginLeft: "6px" }}>
            <SvgToIcon
              IconLogo={IntegrationsMapping[params.value?.toLowerCase()]?.logo}
              width={40}
            />
          </Box>
        </CustomToolTip>
      ),
    },
    {
      field: "is_gap",
      headerName: "Status",
      width: 80,
      sortComparator: (v1, v2) => {
        const statusOrder = {
          [ControlStatus.GAP]: 4,
          [ControlStatus.COMPLIANT]: 3,
          [ControlStatus.SNOOZED]: 2,
          [ControlStatus.EXCLUDED]: 1,
        };

        const value1 = statusOrder[v1] || 0;
        const value2 = statusOrder[v2] || 0;

        return value1 - value2;
      },
      renderCell: (params) => {
        return <ControlStatusIndicator status={params.row?.status} />;
      },
    },
    {
      field: "affected_entity_counts_by_type",
      headerName: "Affected Entities",
      flex: 0.25,
      disableClickEventBubbling: true,
      renderCell: (params) => {
        const countsByType = params.value;
        if (!countsByType || Object.keys(countsByType).length === 0) {
          return null;
        }

        const affectedEntitiesFilterByType = groupEntityIdsByType(
          affectedEntityIdsFilter
        );
        const affectedEntitiesFilterTypes = Object.keys(
          affectedEntitiesFilterByType
        );

        const entries = Object.entries(countsByType);

        // Show only entity type relevant for CVEs
        let filteredEntries = entries.filter(([type]) => {
          return [ComplexEntityTypes.SERVER, ComplexEntityTypes.HOST].includes(
            type
          );
        });

        // Remove types that are not in the filter
        filteredEntries = entries.filter(([type]) =>
          affectedEntitiesFilterTypes.includes(type)
        );

        // Limit counts to not exceed the actual affected entities count in the filter for each type
        filteredEntries = filteredEntries.map(([type, count]) => {
          const affectedCountForType =
            affectedEntitiesFilterByType[type]?.length || 0;
          const limitedCount = Math.min(count, affectedCountForType);
          return [type, limitedCount];
        });

        const firstEntry = filteredEntries[0];
        const remainingEntries = filteredEntries.slice(1);
        const tooltipTitle = remainingEntries
          .map(([type, count]) => getEntitiesLabel(count, type))
          .join(", ");

        // TODO: This will be updated in https://linear.app/breeze-security/issue/DEV-2543/consolidate-entity-chip-components
        return (
          <Box
            display={"flex"}
            flexDirection={"row"}
            gap={1}
            sx={{ marginLeft: "10px" }}
            // Make this box unclickable so it won't confuse the user. Because when clicking on the chip, it will open the remediation drawer.
            onClick={(event) => event.stopPropagation()}
          >
            {firstEntry && (
              <Grid item key={firstEntry[0]}>
                <InvolvedEntityChip
                  type={firstEntry[0]}
                  entitiesCount={firstEntry[1]}
                  setSelectedEntities={null}
                  control={params.row}
                  sx={{ height: "24px" }}
                />
              </Grid>
            )}
            {remainingEntries && remainingEntries.length > 0 && (
              <CustomToolTip title={tooltipTitle}>
                <Chip
                  label={`+${remainingEntries.length}`}
                  style={{
                    minWidth: "24px",
                    background: colors.entity_chip_enabled_fill,
                    boxShadow: colors.entity_chip_enabled_shadow,
                    borderRadius: "8px",
                    fontSize: "12px",
                    color: colors.text_primary,
                    cursor: "auto",
                    userSelect: "none",
                    height: "24px",
                  }}
                />
              </CustomToolTip>
            )}
          </Box>
        );
      },
    },
    //TODO:Add jira ticket field here when the button is separated from - ControlRemediationSection
  ];
}
